import { GoogleAuthProvider } from 'firebase/auth';
import AnalyticsService, { AnalyticsEvents } from '../Services/AnalyticsService';
import ApiService from '../Services/ApiService';
import RegistrationService from '../Services/RegistrationService';
import GeolocationService from '../Services/GeolocationService';
import CookiesService from '../Services/CookiesService';
import DeviceService from '../Services/DeviceService';
import UtilsService from '../Services/UtilsService';
import StorageService from '../Services/StorageService';
import { STORAGE_CONFIG } from '../Services/StorageService.config';
import { CLICK_START_TRIAL_ADDITIONAL_DATA_STORAGE_KEY } from '../constants/constants';
import { trackError } from '../utils/error.utils';
import ABTestToolServiceV2 from '../Services/ABTestToolService.v2';

const parseJwt = token => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            // deprecated just in node js
            atob(base64)
                .split('')
                .map(function (c) {
                    return (
                        '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                    );
                })
                .join('')
        );

        return JSON.parse(jsonPayload);
    } catch (error) {
        trackError('JWT parsing failed in Google One Tap', {
            source: 'google_one_tap',
            platform: 'gatsby',
            error_type: 'jwt_parsing_failure',
            error_message:
                error instanceof Error ? error.message : String(error)
        });
        throw error;
    }
};

export const shouldLoadOneTap = async () => {
    // Check for force parameter in URL
    const forceGoogleOneTapParam =
        UtilsService.getParameterByName('forceGoogleOneTap');
    if (forceGoogleOneTapParam) {
        return true;
    }

    // Check geolocation eligibility
    const isValidGeolocation =
        await GeolocationService.isFromValidCountriesPromise();
    if (!isValidGeolocation) {
        return false;
    }

    // Allow desktop devices in the marketing_site_onetap enabled group
    if (!DeviceService.isMobile){
        return (
            ABTestToolServiceV2.getTestVariantByPath(
                window.location.pathname,
                'marketing_site_onetap'
            ) === 'enabled'
        );
    }

    // Mobile devices are not eligible for Google One Tap
    return false;
};

export const handleOneTapResponse = async result => {
    try {
        const { idToken } = GoogleAuthProvider.credential(result.credential);
        if (!idToken) {
            trackError(
                'Google One Tap credential processing failed - idToken undefined',
                {
                    source: 'google_one_tap',
                    platform: 'gatsby',
                    error_type: 'idToken_undefined',
                    credential_present: !!result.credential
                }
            );
            AnalyticsService.track(AnalyticsEvents.signup_with_google_error, {
                data: 'idToken undefined'
            });
            return;
        }
        const details = parseJwt(result.credential);
        details.credential = result.credential;

        const data = {
            auth_token: idToken,
            id_token: idToken,
            sso_type: 'google'
        };
        const ssoSignupUserResponse = await ApiService.ssoSignupUser(data);
        //if user exist so redirect to app
        if (ssoSignupUserResponse?._id) {
            storeGoogleOneTapDataInCookie(
                ssoSignupUserResponse._id,
                ssoSignupUserResponse.authentication_token
            );
            RegistrationService.handleSSOUserExist(ssoSignupUserResponse);
            return;
        }

        const userData = {
            name: details.name,
            email: details.email,
            auth_token: idToken,
            sso_type: 'google_one_tap',
            auth_code: idToken,
            is_sso: true,
            is_firebase: true,
            profile_img_url: details.picture || ''
        };

        const dataForSignup = RegistrationService.handleRegistration();
        const newData = { ...dataForSignup, userData };

        const additionalDataFromSessionStorage: Record<string, unknown> =
            StorageService.getItem(
                CLICK_START_TRIAL_ADDITIONAL_DATA_STORAGE_KEY,
                'sessionStorage'
            ) || {};

        const additionalDataFromSessionStoragePerPath =
            additionalDataFromSessionStorage[window.location.pathname] || {};

        const additionalDataForClickStartTrial = Object.values(
            additionalDataFromSessionStoragePerPath
        ).reduce<Record<string, unknown>>(
            (acc, curr: Record<string, unknown>) => ({
                ...acc,
                ...curr
            }),
            {}
        );

        AnalyticsService.trackClick(AnalyticsEvents.start_trial, {
            source: 'google_one_tap',
            signup_landing_page:
                UtilsService.getSignupLandingPageFromLocationPath(),
            start_trial_variant: 'single-cta',
            ...additionalDataForClickStartTrial
        });

        setTimeout(() => {
            RegistrationService.onRegistrationSuccessWithoutFields(newData);
        }, 100);
    } catch (error) {
        trackError('Google One Tap response handling failed', {
            source: 'google_one_tap',
            platform: 'gatsby',
            error_type: 'response_handling_failure',
            error_message:
                error instanceof Error ? error.message : String(error),
            has_credential: !!result?.credential
        });
        throw error;
    }

    function storeGoogleOneTapDataInCookie(
        _id: string,
        authentication_token: string
    ) {
        CookiesService.setCookie({
            cookieName: 'hb_google_one_tap_auth',
            cookieValue: JSON.stringify({
                _id,
                authentication_token
            }),
            domain: STORAGE_CONFIG.cookieDomain,
            expirationSeconds: 30
        });
    }
};
